import { useMutation, useQuery } from '@apollo/client/react';
import classNames from 'classnames';
import type { FormEvent } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useLocalAuth } from '../../components/LocalAuthProvider/LocalAuthProvider';
import RevvlyMark from '../../components/Layout/revvly-mark.svg?react';
import LoadingOverlay from '../../components/LoadingOverlay/LoadingOverlay';
import { BIND_MCP_OAUTH_TENANT } from '../../graphql/mcp.operations';
import { GET_MY_TENANTS } from '../../graphql/tenant.operations';

import styles from './OAuthConsent.module.css';
import {
  MCP_READ_SCOPE,
  MCP_WRITE_SCOPE,
  mcpConsentUrl,
  requestedMcpScopes,
  signedOAuthQuery,
} from './oauth-urls';
import type { UntypedData } from '../../apollo/untypedData';

const SCOPE_LABELS: Record<string, string> = {
  [MCP_READ_SCOPE]: 'Read billing and catalog data',
  [MCP_WRITE_SCOPE]: 'Create and change records after you confirm each action',
  openid: 'Sign you in',
  profile: 'Name and profile',
  email: 'Email address',
  offline_access: 'Stay connected',
};

interface TenantRow {
  tenantId: string;
  lastAccessedAt?: string | Date | null;
  tenant?: { id: string; name: string } | null;
}

export default function OAuthConsent() {
  const { isAuthenticated, isLoading: authLoading } = useLocalAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const oauthQuery = useMemo(() => signedOAuthQuery(searchParams), [searchParams]);
  const clientId = searchParams.get('client_id') ?? '';
  const scopes = useMemo(() => requestedMcpScopes(searchParams), [searchParams]);

  const [selectedId, setSelectedId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { data, loading: tenantsLoading } = useQuery<UntypedData>(GET_MY_TENANTS, {
    skip: !isAuthenticated,
    fetchPolicy: 'network-only',
  });
  const [bindTenant] = useMutation<UntypedData>(BIND_MCP_OAUTH_TENANT);

  const tenants: TenantRow[] = useMemo(() => data?.myTenants ?? [], [data]);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      const returnTo = `/oauth/consent?${searchParams.toString()}`;
      navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`, { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate, searchParams]);

  useEffect(() => {
    if (selectedId || tenants.length === 0) {
      return;
    }
    const preferred = [...tenants].sort((a, b) => {
      const aTime = a.lastAccessedAt ? new Date(a.lastAccessedAt).getTime() : 0;
      const bTime = b.lastAccessedAt ? new Date(b.lastAccessedAt).getTime() : 0;
      return bTime - aTime;
    })[0];
    if (preferred?.tenantId) {
      setSelectedId(preferred.tenantId);
    }
  }, [selectedId, tenants]);

  const handleConsent = async (accept: boolean) => {
    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      if (accept) {
        if (!selectedId) {
          setErrorMessage('Select a workspace to continue.');
          return;
        }
        await bindTenant({ variables: { tenantId: selectedId } });
      }
      const response = await fetch(mcpConsentUrl(), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ accept, oauth_query: oauthQuery }),
      });
      const payload = (await response.json().catch(() => ({}))) as { url?: string };
      if (!response.ok || !payload.url) {
        throw new Error(
          'Authorization could not be completed. Return to the client and try again.',
        );
      }
      window.location.assign(payload.url);
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Authorization could not be completed.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    void handleConsent(true);
  };

  if (authLoading || (isAuthenticated && tenantsLoading)) {
    return <LoadingOverlay />;
  }

  if (!isAuthenticated) {
    return <LoadingOverlay />;
  }

  if (!oauthQuery) {
    return (
      <div className={styles.Page}>
        <div className={styles.Card}>
          <RevvlyMark className={styles.Mark} aria-hidden="true" />
          <h1 className={styles.Title}>Authorization expired</h1>
          <p className={styles.Subtitle}>
            This request is no longer valid. Return to the client and connect again.
          </p>
        </div>
      </div>
    );
  }

  if (tenants.length === 0) {
    return (
      <div className={styles.Page}>
        <div className={styles.Card}>
          <RevvlyMark className={styles.Mark} aria-hidden="true" />
          <h1 className={styles.Title}>No workspace access</h1>
          <p className={styles.Subtitle}>
            Your account is not a member of any Revvly workspace, so this client cannot be
            connected.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.Page}>
      <div className={styles.Card}>
        <RevvlyMark className={styles.Mark} aria-hidden="true" />
        <h1 className={styles.Title}>Connect to Revvly</h1>
        <p className={styles.Subtitle}>
          {scopes.includes(MCP_WRITE_SCOPE) ? (
            <>
              This application can read billing data and propose writes for one workspace: clients,
              subscriptions, catalog, and invoices. Each write returns a proposal; it executes only
              after you review and approve it while signed in to Revvly (
              <code className={styles.ScopeCode}>{MCP_WRITE_SCOPE}</code>). The connected
              application cannot approve its own proposals.
            </>
          ) : (
            <>
              This application can read billing data for one workspace: clients, subscriptions,
              invoices, analytics, engagement, and catalog. Access is read-only (
              <code className={styles.ScopeCode}>{MCP_READ_SCOPE}</code>
              ). It cannot create or change records.
            </>
          )}
        </p>
        {clientId && <p className={styles.ClientId}>Client {clientId}</p>}
        <ul className={styles.ScopeList} aria-label="Requested access">
          {scopes.map((scope) => (
            <li key={scope} className={styles.ScopeItem}>
              <code className={styles.ScopeCode}>{scope}</code>
              <span>{SCOPE_LABELS[scope] ?? 'Requested by the client'}</span>
            </li>
          ))}
        </ul>

        {errorMessage && <div className={styles.Error}>{errorMessage}</div>}

        <form className={styles.Form} onSubmit={onSubmit}>
          <p className={styles.Label}>Workspace</p>
          <ul className={styles.TenantList}>
            {tenants.map((row) => {
              const name = row.tenant?.name || row.tenantId;
              const isSelected = selectedId === row.tenantId;
              return (
                <li key={row.tenantId}>
                  <button
                    type="button"
                    className={classNames(styles.Tenant, { [styles.TenantSelected]: isSelected })}
                    onClick={() => setSelectedId(row.tenantId)}
                    aria-pressed={isSelected}
                  >
                    {name}
                  </button>
                </li>
              );
            })}
          </ul>

          <div className={styles.Actions}>
            <button
              type="button"
              className={styles.Secondary}
              disabled={isSubmitting}
              onClick={() => void handleConsent(false)}
            >
              Cancel
            </button>
            <button type="submit" className={styles.Primary} disabled={isSubmitting || !selectedId}>
              {isSubmitting ? 'Connecting...' : 'Allow access'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
